import type { LeatherbackState } from './types';
import { civilFromEpochDay, dayOfYear, epochDayFromCivil } from './calendar';
import { YEAR_DAYS, dailyDeath, type PopulationDraws } from './population';

/**
 * The western Pacific leatherback turtle (M111): the whole population, one
 * stock, NOAA's West Pacific population, which nests on the beaches of
 * Papua Barat, Papua New Guinea, the Solomon Islands and Vanuatu, just south
 * of the equator, and feeds as far as the South China Sea, California and
 * the Tasman Sea: the game's third population of no sanctuary, the
 * Pacific's low bands'. Counted by sex and year class since hatching, each
 * class aged on the first of January, the 17-year-olds' turning adult on the
 * day their mean age reaches 17 (`matureDayOfYear`); its females nesting in
 * two seasons a year, each season's nesters drawn on its first day from the
 * adult females; their hatchlings, of the nests the predators left,
 * reaching the sea an incubation after their eggs were laid; every turtle
 * dying at its stage's rate. Its draws are the caller's, as the
 * right whales' and the marlin's are: binomial deaths and nesters and Poisson
 * hatchlings on its own stream in the game, expected values in its table
 * (`scripts/leatherback.ts`).
 *
 * **Its rates are measured on its own beaches, its adults' survival on one of
 * them, and the young's survival the one fitted number**: the clutches a
 * nester lays, the eggs a clutch and the share that hatch at Jamursba Medi
 * and Wermon, Papua Barat, the two beaches NOAA's counts are made on; the
 * adult females' survival tagged at Kamiali, Papua New Guinea; the age of
 * maturity read in Pacific leatherbacks' bones. No source served here
 * measures how many hatchlings live to nest (M111's readers: Jones et al.
 * 2012 assume 25% a year in the first and derive 0.81 after it from assumed
 * inputs), so the young's survival a year, every year until maturity, is set
 * (`data/leatherbacks/`) so the population falls at the rate NOAA's model of
 * the two beaches' nests found, 6.1% a year from 2001 to 2017 (Martin et al.
 * 2020, NOAA Tech. Memo. NMFS-PIFSC-95), which NOAA takes as "the best
 * available information" in 2026. Its table is scaled to NOAA's estimate of
 * the population's adult females in 2025.
 *
 * **Where it is**: a season's nesters are at their beaches, in the South
 * Pacific's low band, for the days their clutches take, the rest of the
 * season on their way (`atBeaches`); the adults that nest in the northern
 * summer feed in the North Pacific's low band, those that nest in its winter
 * in the South Pacific's, as the satellite tags followed them
 * (`foragingNorth`). The young are not placed: no source tracks them (open).
 */

/** A nesting season: when its eggs are laid, its share of the nesters, and its beach's eggs and hatching. */
export interface NestingSeason {
  id: 'summer' | 'winter';
  /** The months it lays in, calendar months 1 to 12, in order from its first: a season belongs to the year it begins in. */
  months: readonly number[];
  /** Its share of each year's nesting females. */
  share: number;
  /** Eggs a clutch, at the beach it is measured on; the share of nests lost whole to predators there (feral pigs, and at Wermon dogs too); the share of the eggs of the nests left that hatch. */
  eggsPerClutch: number;
  nestsLost: number;
  hatching: number;
}

/** A leatherback's life, each figure with its source. */
export interface LeatherbackLife {
  id: 'west_pacific_leatherback';
  /** The mean age it is mature (an adult, nesting) from, years: a knife edge at that mean (`matureDayOfYear`). */
  matureYears: number;
  /** The oldest class, years: it holds every turtle of that age and over. */
  oldestYears: number;
  /** An adult's survival a year, either sex. */
  adultSurvival: number;
  /** The share of each season's hatchlings female. */
  femaleShare: number;
  /** The mean years between an adult female's nesting seasons: one over it nest a year. */
  remigrationYears: number;
  /** Clutches a nester lays in its season, and the days between them. */
  clutches: number;
  internestingDays: number;
  /** Days from an egg laid to its hatchling reaching the sea. */
  incubationDays: number;
  /** The two nesting seasons: the northern summer's, then its winter's. */
  seasons: readonly [NestingSeason, NestingSeason];
  /** NOAA's estimate of the population's adult females: the table's, on the first of January a world opens on. */
  adultFemales: number;
  /** The population's growth rate a year, r (its factor e^r): the young's survival is set to it. */
  growthRate: number;
}

/**
 * The western Pacific leatherback, *Dermochelys coriacea*, each figure with
 * its source as read (M111). Second-hand figures say whose compilation they
 * were read in: the papers behind them sit on hosts this environment refuses
 * (M111's readers).
 */
export const WEST_PACIFIC_LEATHERBACK: LeatherbackLife = {
  id: 'west_pacific_leatherback',
  /**
   * "for Pacific leatherbacks, mean maturation CCL back-calculated for the
   * transition LAG was 129.3 cm (range 110.7–152.3 cm CCL) and mean age was
   * 17 years (range 12–28 years)" (Avens et al. 2020, Marine Biology 167:4,
   * p. 5): the growth marks in the bones of Pacific leatherbacks dead in
   * fisheries, read where their growth slows at maturity. Every turtle mature
   * at 17, a knife edge at the mean (open: the spread of 12 to 28): the
   * classes age on the first of January, so the 17-year-olds' class holds
   * turtles 16.1 to 17.0 years old on that day, and turns adult on the day
   * its hatchlings' mean age reaches 17 (`matureDayOfYear`; M111's review).
   * NOAA's models take 16.1 from a growth curve fitted to captive turtles
   * (Martin et al. 2020, Appendix II).
   */
  matureYears: 17,
  /** "maximum lifespan estimates of 50 years and 46 years for the Atlantic and Pacific, respectively" (Avens et al. 2020, p. 9), the oldest maturing turtle's age and the longest adult life its bones record: the classes run to 46, the last every leatherback of 46 or older. */
  oldestYears: 46,
  /**
   * "Female survivorship (percent) Papua New Guinea: 0.85 (95 percent CI:
   * 0.66 – 0.95) Pilcher and Chaloupka 2013" (NMFS and USFWS 2020, the
   * status review, Table 24, p. 232), from "capture-mark-recapture data for
   * 178 nesting leatherback sea turtles tagged at Kamiali beach, PNG over a
   * 10-year boreal winter nesting period (2000-2009)" (NMFS 2019, the
   * biological opinion on Hawaiʻi's shallow-set longline fishery, p. 65):
   * the population's only measured adult survival, every death
   * in it (the fisheries' among them: NOAA's own models take 0.893, measured
   * at St. Croix in the Atlantic, Martin et al. 2020, Appendix II). The
   * males' is unmeasured; taken as the females' (open).
   */
  adultSurvival: 0.85,
  /**
   * "proportion female = sex ratio 0.73 ... Using Benson et al 2011
   * (Ecosphere) 27:10 female:male (27 females and 10 males
   * captured/sat-tagged) as best (only) available estimate even though CA
   * foragers different foraging stock and size ranges that are not
   * necessarily representative" (Martin et al. 2020, Appendix II, p. 86):
   * 27 of 37. The hatchlings are given it, so the adults keep it, both sexes
   * dying alike; Indonesia's beaches make "Female-biased sex ratios"
   * (the status review, Table 24, p. 233), by how much unmeasured (open).
   */
  femaleShare: 27 / 37,
  /**
   * "the mean remigration interval (RI) (3.06 for leatherbacks ...)", a
   * distribution fitted "to known remigration intervals of leatherbacks from
   * the North Pacific Transition Zone and the Northeast Pacific reported in
   * Lontoh (2014)" (Martin et al. 2020, p. 15); "Indonesia: 2 – 4" (the
   * status review, Table 24, p. 232).
   */
  remigrationYears: 3.06,
  /**
   * "Observed clutch frequencies ranged from 3-10 per season with a mean of
   * 5.5 ± 1.6" (Tapilatu et al. 2013, Ecosphere 4(2):25, p. 1), the females
   * tagged at Jamursba Medi and Wermon, 2003 to 2011.
   */
  clutches: 5.5,
  /** "Internesting interval (days) ... Indonesia: 9 Lontoh 2014" (the status review, Table 24, pp. 232-233): a nester's 5.5 clutches keep her at her beach 40.5 days. */
  internestingDays: 9,
  /** "Incubation period (days) ... Indonesia: 61.5 (SD 4.7) Tapilatu and Tiwari 2007" (the status review, Table 24, p. 233). */
  incubationDays: 61.5,
  seasons: [
    /**
     * "This DPS nests year round, and exhibits a bimodal nesting strategy
     * whereby a proportion of females nest during November through February
     * (i.e., “winter” nesting females) and other females nest May through
     * September (i.e., “summer” nesting females" (the status review, p. 216);
     * "we estimate that more females nested at the Bird’s Head beaches each
     * year during the boreal summer than during the austral summer season,
     * with 72% of nesting on average occurring during the boreal summer
     * between 2005 and 2011" (Tapilatu et al. 2013, p. 7), its seasons
     * April to September and October to March, "No turtles were ever
     * observed to nest during both" (p. 6). The other beaches (a quarter of
     * the nesting, by NOAA's assumption) nest "primarily during winter"
     * (the status review, p. 226), their share unmeasured: the two beaches'
     * split is the population's (open). The summer's beach is Jamursba
     * Medi, where "Summer nesting (July peak) is mostly confined" (p. 26):
     * its eggs a clutch, 79.6 (n = 48), "Tapilatu and Tiwari 2007" (Table
     * 24, p. 232); "Hatching success of nests undisturbed by feral pig
     * predation was significantly lower in Jamursba-Medi (25.5 percent)
     * than Wermon (47.1 percent)" (p. 251); "Intensive management effort at
     * Jamursba-Medi reduced feral pig predation of nests to five percent
     * during the 2016 and 2017 nesting seasons" (p. 240), the nests lost
     * whole. Nests lost to the sea and eggs taken are not counted (open).
     */
    { id: 'summer', months: [5, 6, 7, 8, 9], share: 0.72, eggsPerClutch: 79.6, nestsLost: 0.05, hatching: 0.255 },
    /** Wermon's, which peaks in the winter (Tapilatu et al. 2013, p. 5): 76.2 eggs a clutch (n = 51), 47.1% hatching (n = 52) (the status review, Table 24, pp. 232-233, and p. 251); "At Wermon, 21 percent of nests were lost to predation during the 2004/2005 nesting season (Wurlianty and Hitipeuw 2005)" (p. 240), the latest measured there. */
    { id: 'winter', months: [11, 12, 1, 2], share: 0.28, eggsPerClutch: 76.2, nestsLost: 0.21, hatching: 0.471 },
  ],
  /**
   * "the total number of adult females in the West Pacific population would
   * be 1,054 (790/0.75) females with an overall 95% CI of 888 to 1,256
   * females" (NMFS 2026, the supplemental biological opinion on Hawaiʻi's
   * deep-set longline fishery, p. 27): Martin et al. 2020's 790 at the two
   * beaches in 2017, which "represent 75% of the total" by NOAA's
   * assumption, unchanged to 2025 ("we also assume that the 6% per decline
   * did not occur between 2017 and 2025", ibid.).
   */
  adultFemales: 1054,
  /**
   * "The Bayesian state-space model suggests the leatherback population
   * (based on annual nesters) is decreasing at 6.1% per year (r = −0.061)"
   * (Martin et al. 2020, p. 24), the two beaches' nests "2001 to 2017"; "95%
   * CI: −23.8% to 12.2%" (p. ix). NOAA in 2026: "use the decline of 6% per
   * year from Martin et al. (2020a) as the best available information ...
   * we also assume that the 6% per decline did not occur between 2017 and
   * 2025, but that it may resume after 2025" (NMFS 2026, p. 27).
   */
  growthRate: -0.061,
};

/**
 * The share of the adults feeding in the North Pacific's low band: the
 * summer nesters', who go there, "About one third of deployments ...
 * westward into the Sulawesi, Sulu, and South China Seas ... The other two
 * thirds of post-nesting females traveled northeastward into waters of the
 * western, central, or eastern temperate North Pacific, including the most
 * distant foraging grounds off California, Oregon and Washington"; the winter
 * nesters feed in the South Pacific's, "Nearly all females nesting in the
 * Solomon Islands and Papua New Guinea traveled southward into waters off SE
 * Australia and northern New Zealand", and of Papua Barat's "only 35 percent
 * ... moved southward into waters off Australia and New Zealand, while the
 * remaining 65 percent moved west and south into the Halmahera, Ceram, or
 * Banda Seas" (the status review, p. 26, of Benson et al. 2011's tags,
 * "126 satellite tag deployments" on nesters and off central California,
 * Figure 40, p. 218). The
 * males' grounds are taken as the females' (open), and Oregon's and
 * Washington's, north of 40°N, as the low band's (open).
 */
export const foragingNorth = (life: LeatherbackLife): number => (life.seasons[0] as NestingSeason).share;

/** What the balance sets (`data/leatherbacks/`). */
export interface LeatherbackSupply {
  /** The young's survival a year, from hatching to maturity: fitted to the population's growth rate. */
  youngSurvival: number;
}

/** The classes: 0 to `oldestYears`, the last every turtle of that age or older. */
export const leatherbackClasses = (life: LeatherbackLife): number => life.oldestYears + 1;

/** The days from an egg laid to its hatchling at sea, to the day (61.5 to 62). */
export const incubationOf = (life: LeatherbackLife): number => Math.round(life.incubationDays);

/** A season's laying in the year it begins: its first day, as an epoch day, and its days. */
export function seasonSpan(season: NestingSeason, year: number): { first: number; days: number } {
  const months = season.months;
  const firstMonth = months[0] as number;
  const lastMonth = months[months.length - 1] as number;
  const first = epochDayFromCivil(year, firstMonth, 1);
  const endYear = lastMonth < firstMonth ? year + 1 : year;
  // December's end the next year's 1 January (the calendar's definition of month 12).
  const end = lastMonth === 12 ? epochDayFromCivil(endYear + 1, 1, 1) : epochDayFromCivil(endYear, lastMonth + 1, 1);
  return { first, days: end - first };
}

/** How far into a season's laying a day is (0 its first day) and its days, or null outside it: the season begun in the day's year or the year before. */
export function layingDay(season: NestingSeason, epochDay: number): { day: number; days: number } | null {
  const year = civilFromEpochDay(epochDay).year;
  for (const y of [year, year - 1]) {
    const { first, days } = seasonSpan(season, y);
    const day = epochDay - first;
    if (day >= 0 && day < days) return { day, days };
  }
  return null;
}

/** The hatchlings a nester's season sends to the sea, on average: its clutches and eggs, of the nests the predators left, hatching. */
export const hatchlingsPerNester = (life: LeatherbackLife, season: NestingSeason): number => life.clutches * season.eggsPerClutch * (1 - season.nestsLost) * season.hatching;

/** The days a nester is at her beach: from her first clutch to her last. */
export const beachDays = (life: LeatherbackLife): number => (life.clutches - 1) * life.internestingDays;

/**
 * The day of the year (1 to 366) the 17-year-olds' class turns adult: the day
 * its hatchlings' mean age, as they reached the sea, reaches `matureYears`.
 * The classes age on the first of January, so a hatchling at sea on a day h
 * of a year is in class 1 the next first of January, its age then the share
 * of a year from h to it; each season's hatchlings reach the sea evenly over
 * its hatching days (2001's, a common year: the summer's season of 2001, the
 * winter's begun that November), weighted by its share of the nesters times
 * its hatchlings a nester (`hatchlingsPerNester`). Their mean age on entering
 * class 1, f, makes the class `matureYears` (matureYears − 1 + f) on its first
 * of January, `matureYears` 1 − f of a year later: day 187. Weighted instead
 * by the turtles alive at 17 (the winter's hatchlings, older on entering class
 * 1, a little fewer), day 195 (MEASURED over a year of the table's own
 * hatchlings, M111's second review); those living to maturity do so at a mean
 * of 16.97 years (`toMaturity`, `scripts/leatherback.ts`). A life's, worked
 * out once.
 */
const matureDays = new Map<LeatherbackLife, number>();
export function matureDayOfYear(life: LeatherbackLife): number {
  let day = matureDays.get(life);
  if (day === undefined) {
    const incubation = incubationOf(life);
    let weight = 0;
    let ageSum = 0;
    for (const season of life.seasons) {
      const { first, days } = seasonSpan(season, 2001);
      const w = season.share * hatchlingsPerNester(life, season);
      for (let d = 0; d < days; d++) {
        const h = first + d + incubation;
        const nextJanuary = epochDayFromCivil(civilFromEpochDay(h).year + 1, 1, 1);
        ageSum += (w / days) * ((nextJanuary - h) / YEAR_DAYS);
      }
      weight += w;
    }
    day = 1 + Math.round((1 - ageSum / weight) * YEAR_DAYS);
    matureDays.set(life, day);
  }
  return day;
}

/** Whether a class is adult on a day: every class past `matureYears`, and that class from its day of the year on. */
export const isAdult = (life: LeatherbackLife, cls: number, epochDay: number): boolean =>
  cls > life.matureYears || (cls === life.matureYears && dayOfYear(epochDay) >= matureDayOfYear(life));

/** The adult females on a day. */
export function adultFemales(life: LeatherbackLife, w: LeatherbackState, epochDay: number): number {
  let n = 0;
  for (let c = life.matureYears; c < leatherbackClasses(life); c++) if (isAdult(life, c, epochDay)) n += w.females[c] as number;
  return n;
}

/** On the first of January, every class a year older, the oldest gathering every turtle past its age; on any other day, nothing. */
function ageLeatherbacks(life: LeatherbackLife, w: LeatherbackState, epochDay: number): void {
  if (dayOfYear(epochDay) !== 1) return;
  const oldest = leatherbackClasses(life) - 1;
  for (const counts of [w.females, w.males]) {
    counts[oldest] = (counts[oldest] as number) + (counts[oldest - 1] as number);
    for (let c = oldest - 1; c > 0; c--) counts[c] = counts[c - 1] as number;
    counts[0] = 0;
  }
}

/**
 * A day of the leatherbacks' lives, the classes aged on the first of
 * January (`ageLeatherbacks`):
 *
 * 1. **Deaths**, each class's at its stage's rate on the day (`isAdult`):
 *    the young's at the fitted survival, the adults' at theirs.
 * 2. **Nesters**: on a season's first day, its share of the adult females
 *    over the remigration interval come to nest (`nesters`, kept through
 *    the season and its hatching).
 * 3. **Hatchlings reach the sea**, an incubation after each day's eggs: a
 *    season's nesters' hatchlings spread evenly over its laying days, split
 *    by sex at `femaleShare`, into class 0.
 */
export function stepLeatherbacks(life: LeatherbackLife, w: LeatherbackState, epochDay: number, supply: LeatherbackSupply, draws: PopulationDraws): void {
  ageLeatherbacks(life, w, epochDay);
  const classes = leatherbackClasses(life);
  const young = dailyDeath(supply.youngSurvival);
  const adult = dailyDeath(life.adultSurvival);
  // 1. Deaths: from the first adult class on the day (the 17-year-olds from their day of the year), the adults' rate.
  const adultFrom = isAdult(life, life.matureYears, epochDay) ? life.matureYears : life.matureYears + 1;
  for (const counts of [w.females, w.males]) {
    for (let c = 0; c < classes; c++) {
      const n = counts[c] as number;
      if (n > 0) counts[c] = n - draws.share(n, c < adultFrom ? young : adult);
    }
  }
  const incubation = incubationOf(life);
  life.seasons.forEach((season, s) => {
    // 2. Nesters, on the season's first day.
    if (layingDay(season, epochDay)?.day === 0) {
      w.nesters[s] = draws.share(adultFemales(life, w, epochDay), season.share / life.remigrationYears);
    }
    // 3. Hatchlings, from the eggs laid an incubation ago.
    const laid = layingDay(season, epochDay - incubation);
    if (laid === null) return;
    const hatchlings = ((w.nesters[s] as number) * hatchlingsPerNester(life, season)) / laid.days;
    w.females[0] = (w.females[0] as number) + draws.count(hatchlings * life.femaleShare);
    w.males[0] = (w.males[0] as number) + draws.count(hatchlings * (1 - life.femaleShare));
  });
}

export function emptyLeatherbacks(life: LeatherbackLife): LeatherbackState {
  const none = (): number[] => Array<number>(leatherbackClasses(life)).fill(0);
  return { females: none(), males: none(), nesters: life.seasons.map(() => 0) };
}

/** What the card reads on a day: the whole population, its adults and young by sex, this year's hatchlings, the season's nesters and those at their beaches, and the adults in each low band. */
export interface LeatherbackCounts {
  population: number;
  adultFemales: number;
  adultMales: number;
  young: number;
  /** Class 0: the hatchlings at sea since the first of January. */
  hatchlings: number;
  /** The season laying today, its nesters and those at their beaches today, or null between seasons. */
  nesting: { season: NestingSeason['id']; nesters: number; atBeaches: number } | null;
  north: number;
  south: number;
}

export function leatherbackCounts(life: LeatherbackLife, w: LeatherbackState, epochDay: number): LeatherbackCounts {
  const sum = (xs: readonly number[]): number => xs.reduce((a, b) => a + b, 0);
  const females = adultFemales(life, w, epochDay);
  let males = 0;
  for (let c = life.matureYears; c < leatherbackClasses(life); c++) if (isAdult(life, c, epochDay)) males += w.males[c] as number;
  const s = life.seasons.findIndex((season) => layingDay(season, epochDay) !== null);
  // A season's nesters arrive through its laying, each at her beach for her clutches' days: on a day of it, those days'
  // share of the season's nesters (`atBeaches`), the rest on their way or home.
  const laying = s < 0 ? null : layingDay(life.seasons[s] as NestingSeason, epochDay);
  const nesting = s < 0 || laying === null ? null : {
    season: (life.seasons[s] as NestingSeason).id,
    nesters: w.nesters[s] as number,
    atBeaches: (w.nesters[s] as number) * Math.min(1, beachDays(life) / laying.days),
  };
  // The summer's nesters at their beaches are in the south; the winter's are south either way.
  const adults = females + males;
  const away = nesting?.season === 'summer' ? Math.min(nesting.atBeaches, foragingNorth(life) * females) : 0;
  const north = foragingNorth(life) * adults - away;
  return {
    population: sum(w.females) + sum(w.males),
    adultFemales: females,
    adultMales: males,
    young: sum(w.females) + sum(w.males) - adults,
    hatchlings: (w.females[0] as number) + (w.males[0] as number),
    nesting,
    north,
    south: adults - north,
  };
}
